/**
 * Pure grid decisions for the soil programme review screen (U4).
 *
 * No React, no network, no i18n — every function here is a data transform, which
 * is why it lives in its own module: it is the part worth testing exhaustively,
 * and the page above it stays presentational.
 *
 * Columns are keyed by POSITION, not by month|product. The screen this replaces
 * keyed them by month|product, so two duplicate CSV columns collapsed into one
 * and one column's kg silently vanished — while the validation panel told the
 * user there was a duplicate to fix. Keeping them distinct is what makes §4.6's
 * "Change month on column N / Delete column N" a real fix.
 */
import { SoilProgramEntry, SoilProgramValidation, SoilSuggestion } from '../../services/soilProgramService';
import { naturalCompare } from '../../utils/naturalCompare';

export interface GridColumn { id: string; month: string; productName: string }
export interface GridState {
  orchards: string[];                        // CSV orchard names, natural order
  columns: GridColumn[];
  cells: Record<string, number>;             // cellKey(orchard, colId) -> kg
  orchardMap: Record<string, string>;        // CSV orchard -> catalogue orchard
  productMap: Record<string, string>;        // CSV product -> catalogue product
  columnErrors: Record<string, boolean>;     // colId -> a month edit was rejected as a duplicate
}
export interface GridRow {
  orchardName: string;                       // the CSV name — the identity key
  displayName: string;                       // orchardMap[o] || o
  total: number;
  hasError: boolean;
  isEmpty: boolean;
}

export const cellKey = (orchard: string, colId: string): string => `${orchard}|${colId}`;

/**
 * Allocates one column per (month, productName) occurrence, but a NEW column is
 * only created when the current column for that pair already holds a value for
 * THIS orchard — i.e. it's the same orchard repeating the pair, which is a real
 * duplicate column, not just many orchards legitimately sharing one column.
 */
export function buildState(entries: SoilProgramEntry[]): GridState {
  const orchardSet: Record<string, true> = {};
  const columns: GridColumn[] = [];
  const cells: Record<string, number> = {};
  // pairKey -> list of column ids allocated so far for that (month, product) pair
  const pairColumns: Record<string, string[]> = {};

  entries.forEach((entry) => {
    orchardSet[entry.orchardName] = true;
    const pairKey = `${entry.month}|${entry.productName}`;
    const existingCols = pairColumns[pairKey] || [];
    // Find a column for this pair that does NOT yet hold a value for this orchard.
    let targetColId = existingCols.find((colId) => cells[cellKey(entry.orchardName, colId)] === undefined);
    if (targetColId === undefined) {
      targetColId = `col-${columns.length}`;
      columns.push({ id: targetColId, month: entry.month, productName: entry.productName });
      pairColumns[pairKey] = [...existingCols, targetColId];
    }
    cells[cellKey(entry.orchardName, targetColId)] = entry.kg;
  });

  const orchards = Object.keys(orchardSet).sort(naturalCompare);

  return { orchards, columns, cells, orchardMap: {}, productMap: {}, columnErrors: {} };
}

export function orderedColumns(state: GridState): GridColumn[] {
  return [...state.columns].sort((a, b) => {
    const byMonth = naturalCompare(a.month, b.month);
    if (byMonth !== 0) return byMonth;
    const byProduct = naturalCompare(a.productName, b.productName);
    if (byProduct !== 0) return byProduct;
    return naturalCompare(a.id, b.id);
  });
}

export function displayProduct(state: GridState, col: GridColumn): string {
  return state.productMap[col.productName] || col.productName;
}

export function buildRows(state: GridState, validation?: SoilProgramValidation): GridRow[] {
  return state.orchards.map((orchardName) => {
    const total = state.columns.reduce((sum, col) => sum + (state.cells[cellKey(orchardName, col.id)] || 0), 0);
    const mapped = !!state.orchardMap[orchardName];
    const implicated = !!validation && (
      validation.unknownOrchards.includes(orchardName) || validation.duplicateOrchardRows.includes(orchardName)
    );
    return {
      orchardName,
      displayName: state.orchardMap[orchardName] || orchardName,
      total,
      hasError: implicated && !mapped,
      isEmpty: total <= 0,
    };
  });
}

export function columnTotals(state: GridState): Record<string, number> {
  const totals: Record<string, number> = {};
  state.columns.forEach((col) => {
    totals[col.id] = state.orchards.reduce((sum, orchardName) => sum + (state.cells[cellKey(orchardName, col.id)] || 0), 0);
  });
  return totals;
}

export function grandTotal(state: GridState): number {
  return Object.values(columnTotals(state)).reduce((sum, v) => sum + v, 0);
}

/** True when two columns (by id) currently share the same (month, displayed product). */
function sharesPairWithAnother(state: GridState, col: GridColumn): boolean {
  const month = col.month;
  const product = displayProduct(state, col);
  return state.columns.some((other) => (
    other.id !== col.id && other.month === month && displayProduct(state, other) === product
  ));
}

export function columnHasError(state: GridState, col: GridColumn, validation?: SoilProgramValidation): boolean {
  const unknownProduct = !!validation
    && validation.unknownProducts.includes(col.productName)
    && !state.productMap[col.productName];
  return unknownProduct || sharesPairWithAnother(state, col);
}

/**
 * The columns a reported duplicate-column issue implicates, for rendering its fix
 * affordances. The issue's `productName` is the RAW CSV name when the server's
 * validation found it and the MAPPED display name when the (month, displayProduct)
 * collision found it, so BOTH identities are matched here — matching only raw
 * would leave a mapped collision counted in the panel but with no way to fix it.
 */
export function columnsForDuplicateIssue(state: GridState, month: string, productName: string): GridColumn[] {
  return state.columns.filter((c) => (
    c.month === month && (c.productName === productName || displayProduct(state, c) === productName)
  ));
}

export function setCell(state: GridState, orchard: string, colId: string, kg: number): GridState {
  return { ...state, cells: { ...state.cells, [cellKey(orchard, colId)]: kg } };
}

export function deleteRow(state: GridState, orchard: string): GridState {
  const orchards = state.orchards.filter((o) => o !== orchard);
  const cells: Record<string, number> = {};
  Object.keys(state.cells).forEach((key) => {
    if (key.split('|')[0] !== orchard) cells[key] = state.cells[key];
  });
  const orchardMap = { ...state.orchardMap };
  delete orchardMap[orchard];
  return { ...state, orchards, cells, orchardMap };
}

export function deleteColumn(state: GridState, colId: string): GridState {
  const columns = state.columns.filter((c) => c.id !== colId);
  const cells: Record<string, number> = {};
  Object.keys(state.cells).forEach((key) => {
    // key = `${orchard}|${colId}` — orchard names never contain '|', so the suffix
    // after the first '|' is the colId.
    const suffix = key.slice(key.indexOf('|') + 1);
    if (suffix !== colId) cells[key] = state.cells[key];
  });
  // Deleting a column can only resolve duplicate conflicts, never create one, so
  // clearing every sibling's stale error is correct and simplest.
  return { ...state, columns, cells, columnErrors: {} };
}

export function changeMonth(state: GridState, colId: string, month: string): GridState {
  const col = state.columns.find((c) => c.id === colId);
  if (!col) return state;
  // Both identities are checked. The RAW one keeps a server-reported duplicate
  // from being re-created; the MAPPED one is what `toEntries` actually writes, so
  // a move that lands two columns on the same (month, displayProduct) would emit
  // genuinely colliding entries and lose one column's kg. This is the same
  // identity `columnHasError`/`sharesPairWithAnother` flags, so the guard, the
  // red header and the issue counter now all agree.
  const product = displayProduct(state, col);
  const wouldDuplicate = state.columns.some((other) => (
    other.id !== colId && other.month === month
    && (other.productName === col.productName || displayProduct(state, other) === product)
  ));
  if (wouldDuplicate) {
    return { ...state, columnErrors: { ...state.columnErrors, [colId]: true } };
  }
  const columns = state.columns.map((c) => (c.id === colId ? { ...c, month } : c));
  const columnErrors = { ...state.columnErrors };
  delete columnErrors[colId];
  return { ...state, columns, columnErrors };
}

export function mapOrchard(state: GridState, csvName: string, target: string): GridState {
  return { ...state, orchardMap: { ...state.orchardMap, [csvName]: target } };
}

export function mapProduct(state: GridState, csvName: string, target: string): GridState {
  return { ...state, productMap: { ...state.productMap, [csvName]: target } };
}

export function toEntries(state: GridState): SoilProgramEntry[] {
  const result: SoilProgramEntry[] = [];
  state.orchards.forEach((orchardName) => {
    state.columns.forEach((col) => {
      const kg = state.cells[cellKey(orchardName, col.id)] || 0;
      if (kg > 0) {
        result.push({
          orchardName: state.orchardMap[orchardName] || orchardName,
          month: col.month,
          productName: displayProduct(state, col),
          kg,
        });
      }
    });
  });
  return result;
}

export function remainingIssues(state: GridState, validation?: SoilProgramValidation): {
  unknownProducts: string[]; unknownOrchards: string[];
  duplicateOrchardRows: string[]; duplicateColumns: { month: string; productName: string }[];
  total: number;
} {
  if (!validation) {
    return { unknownProducts: [], unknownOrchards: [], duplicateOrchardRows: [], duplicateColumns: [], total: 0 };
  }

  const columnsCarryingProduct = (productName: string) => state.columns.filter((c) => c.productName === productName);

  const unknownProducts = validation.unknownProducts.filter((productName) => {
    if (state.productMap[productName]) return false;
    return columnsCarryingProduct(productName).length > 0;
  });

  const unknownOrchards = validation.unknownOrchards.filter((orchardName) => {
    if (state.orchardMap[orchardName]) return false;
    return state.orchards.includes(orchardName);
  });

  const duplicateOrchardRows = validation.duplicateOrchardRows.filter((orchardName) => state.orchards.includes(orchardName));

  // A duplicate column is reported when EITHER identity still collides.
  //
  // (a) RAW (never-mutated) column fields, for a server-reported duplicate:
  //     mapping a product is deliberately NOT a resolution path, so a mapped-away
  //     name must keep its blocker. The only resolutions are that the columns no
  //     longer share (month, product), or one of them is gone.
  // (b) MAPPED (month, displayProduct), because that is exactly what `toEntries`
  //     writes. Two columns sharing it emit genuinely colliding entries and one
  //     column's kg is lost — even when their raw names differ (e.g. 'CN' mapped
  //     onto 'Calcium Nitrate' next to a raw 'Calcium Nitrate' column).
  //
  // Both are keyed by (month, displayProduct) for dedupe, so one real collision is
  // counted once no matter how many identities catch it; the reported productName
  // stays the server's raw name whenever (a) is what found it.
  const seenDuplicate: Record<string, true> = {};
  const duplicateColumns: { month: string; productName: string }[] = [];
  const pushDuplicate = (month: string, productName: string, dedupeProduct: string) => {
    const key = `${month}|${dedupeProduct}`;
    if (seenDuplicate[key]) return;
    seenDuplicate[key] = true;
    duplicateColumns.push({ month, productName });
  };
  validation.duplicateColumns.forEach(({ month, productName }) => {
    const matching = state.columns.filter((c) => c.month === month && c.productName === productName);
    if (matching.length >= 2) pushDuplicate(month, productName, displayProduct(state, matching[0]));
  });
  state.columns.forEach((col) => {
    if (!sharesPairWithAnother(state, col)) return;
    const product = displayProduct(state, col);
    pushDuplicate(col.month, product, product);
  });

  const total = unknownProducts.length + unknownOrchards.length + duplicateOrchardRows.length + duplicateColumns.length;

  return { unknownProducts, unknownOrchards, duplicateOrchardRows, duplicateColumns, total };
}

export function duplicateMapping(state: GridState): boolean {
  const targets = state.orchards.map((o) => state.orchardMap[o] || o);
  const unique = Array.from(new Set(targets));
  return unique.length !== targets.length;
}

export function suggestionFor(
  validation: SoilProgramValidation | undefined,
  kind: 'products' | 'orchards',
  name: string,
): SoilSuggestion | undefined {
  if (!validation || !validation.suggestions) return undefined;
  return validation.suggestions[kind].find((s) => s.name === name);
}

/**
 * Which mappings survive a rejected activation.
 *
 * `wasPersisted` says whether the server judged the rows the user is looking
 * at. When it did not, its verdict describes different data, and removing the
 * user's mappings on the strength of it destroys work that was never assessed.
 */
export function mappingsAfterRejection(
  state: GridState,
  validation: SoilProgramValidation,
  wasPersisted: boolean,
): Pick<GridState, 'productMap' | 'orchardMap'> {
  if (!wasPersisted) {
    return { productMap: state.productMap, orchardMap: state.orchardMap };
  }
  const orchardMap = { ...state.orchardMap };
  validation.unknownOrchards.forEach((o) => { delete orchardMap[o]; });
  const productMap = { ...state.productMap };
  validation.unknownProducts.forEach((p) => { delete productMap[p]; });
  return { productMap, orchardMap };
}

export function filterRows(rows: GridRow[], search: string, chip: 'all' | 'errors' | 'empty'): GridRow[] {
  const term = search.toLowerCase();
  return rows.filter((row) => {
    const matchesSearch = !term
      || row.orchardName.toLowerCase().includes(term)
      || row.displayName.toLowerCase().includes(term);
    if (!matchesSearch) return false;
    if (chip === 'errors') return row.hasError;
    if (chip === 'empty') return row.isEmpty;
    return true;
  });
}
